import { useLocation } from "wouter";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PageMeta } from "@/components/PageMeta";
import { ArrowRight, ChevronDown, Shield, Users, Building2, Target } from "lucide-react";

export function FieldManual() {
  const [, setLocation] = useLocation();
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);

  const toggleAccordion = (id: string) => {
    setOpenAccordion(openAccordion === id ? null : id);
  };

  const navToSignIn = () => setLocation("/sign-in");

  return (
    <div className="min-h-screen bg-[#030913] text-white font-sans selection:bg-[#7C6EEF]/30 pb-20">
      <PageMeta
        title="Field Manual — LaunchTactix Growth Playbooks"
        description="Tactical playbooks and step-by-step guides for running autonomous AI growth operations with LaunchTactix — from lead discovery to closed pipeline."
        canonical="https://launchtactix.com/field-manual"
      />
      <nav className="sticky top-0 z-50 bg-[#030913]/90 backdrop-blur-md border-b border-white/5 flex items-center justify-between px-6 py-4">
        <button onClick={() => setLocation("/")} className="hover:opacity-80 transition-opacity">
          <img src={`${import.meta.env.BASE_URL}images/logo-wordmark.png`} alt="LaunchTactix" width={606} height={216} className="h-8 w-auto mix-blend-screen" />
        </button>
        <button
          onClick={navToSignIn}
          className="text-xs font-bold tracking-widest text-white/70 hover:text-white transition-colors uppercase px-5 py-2 border border-white/10 hover:bg-white/5 rounded-none"
        >
          INITIATE
        </button>
      </nav>

      <main className="max-w-4xl mx-auto px-6 pt-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mb-20 text-center"
        >
          <p className="font-mono text-sm tracking-widest text-[#7C6EEF] mb-6 uppercase">// KNOWLEDGE BASE</p>
          <h1 className="text-5xl md:text-7xl font-display font-bold uppercase tracking-tight mb-8">FIELD MANUAL</h1>
          <p className="text-lg text-white/60 leading-relaxed max-w-3xl mx-auto">
            Everything you need to know about deploying LaunchTactix — the modules, the security architecture, privacy, white-labeling, and how the lead engine verifies and prioritizes every contact.
          </p>
          <div className="mt-10">
            <button
              onClick={navToSignIn}
              className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#7C6EEF] text-white font-display font-bold tracking-widest uppercase hover:bg-white hover:text-black transition-colors rounded-none"
            >
              LAUNCH ENGINE →
            </button>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-2xl font-display font-bold uppercase tracking-widest mb-12 border-b border-white/10 pb-4 text-white/80">FIELD ANSWERS.</h2>

          <div className="space-y-16">
            {/* Category: Modules & Growth Team */}
            <div>
              <div className="flex items-center gap-4 mb-8">
                <Users className="w-8 h-8 text-[#7C6EEF]" />
                <h3 className="text-xl font-display font-bold uppercase tracking-widest text-[#7C6EEF]">MODULES & GROWTH TEAM</h3>
              </div>
              <div className="space-y-4">
                <AccordionItem
                  id="mod-1"
                  open={openAccordion}
                  toggle={toggleAccordion}
                  question="HOW DO 70+ AUTONOMOUS MODULES ACTUALLY REPLACE A GROWTH TEAM?"
                  answer="LaunchTactix deploys a full set of autonomous AI systems that collectively perform every role a traditional growth team would cover: lead research, email outreach, reply handling, deal tracking, meeting qualification, SEO content, social publishing, and retention monitoring. Each module runs 24/7 without supervision — the platform self-optimizes sequences, rotates mailboxes when deliverability dips, scores leads in real time, and flags accounts at churn risk before it's visible. One platform replaces the function of a lead researcher, SDR, content writer, SEO strategist, and social manager."
                />
                <AccordionItem
                  id="mod-2"
                  open={openAccordion}
                  toggle={toggleAccordion}
                  question="DO I STILL NEED PEOPLE ON MY TEAM?"
                  answer="LaunchTactix handles the execution layer — the repetitive, high-volume tasks that eat up human time. You still need someone to review strategy, respond to qualified leads, and make business decisions. What changes is the ratio: one person can now manage what previously required 4–6 specialists. Most LaunchTactix customers run leaner teams, redirecting budget from execution headcount to higher-leverage strategic roles."
                />
                <AccordionItem
                  id="mod-3"
                  open={openAccordion}
                  toggle={toggleAccordion}
                  question="IS THERE A FREE WAY TO START?"
                  answer="LaunchTactix does not offer a free tier — but the Growth plan at $249/month includes all 70+ modules with no feature gating. There are no limits on AI usage within the plan. You can cancel any time. We recommend starting with the Growth plan, running the system for 60 days, and measuring the pipeline output before deciding whether to upgrade to Scale or Agency."
                />
              </div>
            </div>

            {/* Category: Security & Compliance */}
            <div>
              <div className="flex items-center gap-4 mb-8">
                <Shield className="w-8 h-8 text-[#7C6EEF]" />
                <h3 className="text-xl font-display font-bold uppercase tracking-widest text-[#7C6EEF]">SECURITY & COMPLIANCE</h3>
              </div>
              <div className="space-y-4">
                <AccordionItem
                  id="sec-1"
                  open={openAccordion}
                  toggle={toggleAccordion}
                  question="IS LAUNCHTACTIX SOC 2 AND ISO 27001 CERTIFIED?"
                  answer="LaunchTactix is actively pursuing SOC 2 Type II and ISO 27001 certification. The platform includes a built-in SOC 2 Readiness Dashboard and ISO 27001 Controls tracker — both accessible from the Compliance section of your workspace. These tools map your current posture against the required controls and identify any gaps. For clients in regulated industries, you can export evidence packs and compliance reports directly from the platform."
                />
                <AccordionItem
                  id="sec-2"
                  open={openAccordion}
                  toggle={toggleAccordion}
                  question="WHAT IS THE SOC 2 READINESS DASHBOARD?"
                  answer="The SOC 2 Readiness Dashboard gives you a live view of your compliance posture across all five Trust Services Criteria: Security, Availability, Processing Integrity, Confidentiality, and Privacy. It tracks control implementation status, identifies gaps, and logs evidence. You can share a read-only compliance view with auditors or clients directly from the platform — no third-party tools required."
                />
                <AccordionItem
                  id="sec-3"
                  open={openAccordion}
                  toggle={toggleAccordion}
                  question="WHAT COMPLIANCE TOOLING IS BUILT IN?"
                  answer="LaunchTactix ships with: unsubscribe management (automatic list suppression), bounce log with threshold alerting, suppression list management, audit log (every platform action is logged with actor and timestamp), SOC 2 readiness tracker, ISO 27001 controls tracker, asset register, vendor register, risk register, and deliverability rules engine. All accessible from the Compliance section of your workspace."
                />
              </div>
            </div>

            {/* Category: Agencies & White-Label */}
            <div>
              <div className="flex items-center gap-4 mb-8">
                <Building2 className="w-8 h-8 text-[#7C6EEF]" />
                <h3 className="text-xl font-display font-bold uppercase tracking-widest text-[#7C6EEF]">AGENCIES & WHITE-LABEL</h3>
              </div>
              <div className="space-y-4">
                <AccordionItem
                  id="agy-1"
                  open={openAccordion}
                  toggle={toggleAccordion}
                  question="CAN I WHITE-LABEL LAUNCHTACTIX FOR CLIENT WORK?"
                  answer="Yes. The Agency tier includes full white-label capability — custom domain, custom logo, custom color scheme, and client-facing portal. Your clients see your brand, not LaunchTactix. You manage all client workspaces from a single Agency dashboard, with full data isolation between clients."
                />
                <AccordionItem
                  id="agy-2"
                  open={openAccordion}
                  toggle={toggleAccordion}
                  question="IS EACH CLIENT'S DATA KEPT SEPARATE?"
                  answer="Yes. Every workspace in LaunchTactix is completely isolated at the database level. Client A cannot see or access Client B's leads, sequences, campaigns, or analytics. Agency owners have a cross-workspace dashboard view for management purposes, but data never bleeds between workspaces."
                />
                <AccordionItem
                  id="agy-3"
                  open={openAccordion}
                  toggle={toggleAccordion}
                  question="CAN I PROVE ROI TO MY CLIENTS?"
                  answer="Yes. LaunchTactix includes a Client Portal — a read-only dashboard you can share with clients showing campaign performance, leads generated, meetings booked, and pipeline value. You can also export full campaign reports and analytics. The Outcomes module tracks every deal back to its originating outreach sequence, giving you attribution data that directly demonstrates ROI."
                />
                <AccordionItem
                  id="agy-4"
                  open={openAccordion}
                  toggle={toggleAccordion}
                  question="WHAT DOES THE AGENCY TIER INCLUDE, AND WHAT DOES IT COST?"
                  answer="The Agency tier is $2,499/month and includes: unlimited workspaces, unlimited mailboxes, unlimited leads, full white-label branding (custom domain + logo + colors), client portal, dedicated support, and access to all 70+ modules across every workspace. It's designed for agencies running growth infrastructure for 5+ clients simultaneously."
                />
              </div>
            </div>

            {/* Category: Lead Verification & Intent */}
            <div>
              <div className="flex items-center gap-4 mb-8">
                <Target className="w-8 h-8 text-[#7C6EEF]" />
                <h3 className="text-xl font-display font-bold uppercase tracking-widest text-[#7C6EEF]">LEAD VERIFICATION & INTENT</h3>
              </div>
              <div className="space-y-4">
                <AccordionItem
                  id="ld-1"
                  open={openAccordion}
                  toggle={toggleAccordion}
                  question="HOW DOES LEAD VERIFICATION WORK?"
                  answer="Every lead discovered by the LaunchTactix lead engine goes through a multi-step verification process: email syntax validation, MX record check, mailbox existence verification (SMTP ping), and a disposable/role-based address filter. Leads that fail verification are flagged and removed from active sequences automatically. Verification runs continuously — leads that were valid at discovery are re-verified before each send to catch address changes."
                />
                <AccordionItem
                  id="ld-2"
                  open={openAccordion}
                  toggle={toggleAccordion}
                  question="HOW DO YOU STOP BAD OR UNQUALIFIED DATA ENTERING THE PIPELINE?"
                  answer="LaunchTactix uses a five-layer data quality system: ICP scoring (only leads matching your Ideal Customer Profile advance), email verification (bad addresses are suppressed before send), intent signal weighting (unverified signals are down-scored), confidence scoring (every lead gets a transparent score explaining why it was or wasn't selected), and a Data Quality Monitor dashboard that shows you exactly where data quality is degrading and why."
                />
                <AccordionItem
                  id="ld-3"
                  open={openAccordion}
                  toggle={toggleAccordion}
                  question="WHAT ARE INTENT SIGNALS, AND HOW ARE THEY USED?"
                  answer="Intent signals are real-time behavioral indicators that suggest an account is actively in a buying window — job postings for roles your product serves, technology installs, funding announcements, hiring velocity spikes, and web traffic patterns. LaunchTactix continuously scans these signals, scores them by strength and recency, and surfaces the highest-intent accounts to the top of your outreach queue. Signal-triggered sequences consistently outperform cold sequences by 3x or more."
                />
                <AccordionItem
                  id="ld-4"
                  open={openAccordion}
                  toggle={toggleAccordion}
                  question="IS EMAIL VALIDATED AS WELL?"
                  answer="Yes. Email validation is a first-class feature in LaunchTactix. Every email address is checked for: valid syntax, working MX record, live SMTP mailbox, and whether it's a catch-all, role-based, or disposable address. Catch-all addresses are flagged with a lower confidence score but not automatically suppressed — you control the threshold. All validation results are logged and visible in the Data Quality Monitor."
                />
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-32 text-center border-t border-white/5 pt-20"
        >
          <h2 className="text-4xl md:text-5xl font-display font-bold uppercase tracking-tight mb-6">SYSTEM READY.</h2>
          <p className="text-lg text-white/50 mb-10">Launch your autonomous growth engine today.</p>
          <button
            onClick={navToSignIn}
            className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#7C6EEF] text-white font-display font-bold tracking-widest uppercase hover:bg-white hover:text-black transition-colors rounded-none"
          >
            LAUNCH ENGINE →
          </button>
        </motion.div>
      </main>

      <footer className="mt-32 py-12 px-6 border-t border-white/5 bg-black">
        <div className="max-w-4xl mx-auto flex flex-col items-center gap-6">
          <img src={`${import.meta.env.BASE_URL}images/logo-wordmark.png`} alt="LaunchTactix" width={606} height={216} className="h-6 w-auto mix-blend-screen opacity-50" />
          <p className="text-[10px] font-mono tracking-widest text-white/30 uppercase">© {new Date().getFullYear()} LaunchTactix. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

function AccordionItem({ id, question, answer, open, toggle }: { id: string; question: string; answer: string; open: string | null; toggle: (id: string) => void }) {
  const isOpen = open === id;
  return (
    <div className="border border-white/10 bg-white/[0.02] rounded-none overflow-hidden">
      <button
        onClick={() => toggle(id)}
        className="w-full text-left px-6 py-5 flex items-center justify-between hover:bg-white/[0.02] transition-colors"
      >
        <span className="font-display font-bold uppercase tracking-wide text-sm pr-4">{question}</span>
        <ChevronDown className={`w-5 h-5 text-white/50 transition-transform ${isOpen ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-6 pt-2 text-white/60 text-sm leading-relaxed border-t border-white/5 mt-2">
              {answer}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
